import React from "react";
import StatusChip from "./StatusChip";

interface Props {
  // true when the buyer has submitted a review at all (seller review OR
  // platform review). false → "Not started".
  hasReview: boolean;
  // Seller-review moderation status. Only seller reviews have a status;
  // platform reviews are not moderated. When `hasReview` is true but
  // `status` is undefined, treat the row as a platform review submission
  // (no moderation state).
  status?: "pending" | "approved" | "rejected";
  // Shortcut for the platform-review row: true means the buyer submitted
  // their platform review. Platform reviews don't go through moderation,
  // so there's no pending/approved/rejected distinction.
  platformSubmitted?: boolean;
}

// Used by the Reviews dashboard to surface, for each purchase, where both
// the seller review and the platform review stand. Seller reviews move
// through the admin moderation workflow (pending → approved/rejected);
// platform reviews are submit-only. All chips use statusChip--wide so the
// longest label ("Under moderation") doesn't clip at the default 100px.
const ReviewStatusChip: React.FC<Props> = ({
  hasReview,
  status,
  platformSubmitted,
}) => {
  const WIDE = "statusChip--xwide";

  if (!hasReview) {
    return (
      <StatusChip
        className={WIDE}
        tone="muted"
        icon="bi-circle"
        label="Not started"
      />
    );
  }

  // Platform review: no moderation state, so just show submitted.
  if (platformSubmitted) {
    return (
      <StatusChip
        className={WIDE}
        tone="success"
        icon="bi-check-circle-fill"
        label="Submitted"
      />
    );
  }

  switch (status) {
    case "approved":
      return (
        <StatusChip
          className={WIDE}
          tone="success"
          icon="bi-check-circle-fill"
          label="Approved"
        />
      );
    case "rejected":
      return (
        <StatusChip
          className={WIDE}
          tone="danger"
          icon="bi-x-circle-fill"
          label="Rejected"
        />
      );
    case "pending":
    default:
      return (
        <StatusChip
          className={WIDE}
          tone="warning"
          icon="bi-hourglass-split"
          label="Under moderation"
        />
      );
  }
};

export default ReviewStatusChip;
